import type { SessionBookingQuote, SubscriptionPackage, UserSubscription } from '@/types/domain'

// Reasons from the member booking quote other than missing credits.
const MEMBER_BOOKING_BLOCKS: Record<string, string> = {
  SESSION_CANCELLED: 'Dieser Termin wurde abgesagt.',
  SESSION_NOT_BOOKABLE: 'Dieser Kurs ist derzeit nicht buchbar.',
  SESSION_IN_PAST: 'Dieser Kurs liegt in der Vergangenheit.',
  REGISTRATION_DEADLINE_PASSED: 'Die Buchungsfrist ist bereits abgelaufen.',
  COURSE_APPROVAL_REQUIRED: 'Für diesen Kurs benötigst du eine Freischaltung. Bitte melde dich bei uns.',
  ALREADY_BOOKED: 'Du bist für diesen Termin bereits angemeldet.',
  SESSION_FULL: 'Dieser Kurs ist leider ausgebucht.',
}

/** Why a member cannot book a session; null if it is bookable or only credits are missing. */
export function memberBookingBlockMessage(
  quote: Pick<SessionBookingQuote, 'can_book' | 'block_reason'> | null | undefined,
): string | null {
  if (!quote || quote.can_book || !quote.block_reason || quote.block_reason === 'INSUFFICIENT_CREDITS') {
    return null
  }
  return MEMBER_BOOKING_BLOCKS[quote.block_reason] ?? 'Dieser Termin kann derzeit nicht gebucht werden.'
}

export function isRecurringPackage(pkg: Pick<SubscriptionPackage, 'billing_mode'>): boolean {
  return pkg.billing_mode === 'subscription'
}

export function isRecurringSubscription(subscription: UserSubscription | null | undefined): boolean {
  return subscription?.billing_mode === 'subscription'
}

export function isSubscriptionActiveAt(
  subscription: UserSubscription | null | undefined,
  referenceDate: Date,
): boolean {
  if (!isRecurringSubscription(subscription) || !subscription?.is_active) {
    return false
  }

  if (
    subscription.stripe_status !== null &&
    !['active', 'trialing'].includes(subscription.stripe_status)
  ) {
    return false
  }

  const now = new Date()
  const expiresAt = subscription.expires_at ? new Date(subscription.expires_at) : null
  if (expiresAt && expiresAt <= now) {
    return false
  }

  const cancellationEnd = subscription.stripe_cancel_at != null
    ? new Date(subscription.stripe_cancel_at)
    : subscription.stripe_cancel_at_period_end
      ? new Date(subscription.current_period_end ?? subscription.expires_at ?? '')
      : null

  if (!cancellationEnd) {
    return true
  }

  return !Number.isNaN(cancellationEnd.getTime()) && cancellationEnd > referenceDate
}

export function formatCreditAmount(count: number): string {
  return new Intl.NumberFormat('de-AT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(count)
}

export function formatCredits(count: number): string {
  return `${formatCreditAmount(count)} ${count === 1 ? 'Credit' : 'Credits'}`
}

export function formatCourseCredits(count: number): string {
  return count === 0 ? 'Kostenlos' : formatCredits(count)
}

export function parseCreditAmount(
  value: string | number,
  options: { allowZero?: boolean } = {},
): number | null {
  const normalized = String(value).trim().replace(',', '.')
  if (!/^\d+(?:\.\d)?$/.test(normalized)) {
    return null
  }

  const parsed = Number(normalized)
  if (!Number.isFinite(parsed) || parsed < 0 || (!options.allowZero && parsed === 0)) {
    return null
  }

  return parsed
}

